import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  Filter,
  X,
  User,
  Shield,
  AlertTriangle,
  CheckCircle2,
  Flag,
  Zap,
  Clock,
  Workflow,
  BarChart3,
  Cpu,
  Phone,
  Mail,
  Globe,
  MessageSquare,
  MessageCircle,
  Building2,
  Laptop,
  Paperclip,
  Loader,
} from 'lucide-react';
import { PayrollCase, getConfidenceLevel } from '../types';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useStore } from '../store/useStore';
import { evaluateCase } from '../utils/confidenceUtils';
import ErrorIndicator from './ErrorIndicator';
import { ProcessingStatus } from '../services/api';
import Tooltip from './Tooltip';
import { API_CONFIG } from '../config/api';

interface CaseListTableProps {
  cases: PayrollCase[];
  showPagination?: boolean;
  processingCases?: ProcessingStatus[];
  showActions?: boolean;
}

type SortField = keyof PayrollCase;
type SortDirection = 'asc' | 'desc';

interface Filters {
  caseNumber: string;
  customer: string;
  status: string;
}

// Filter input components defined outside to prevent recreation on every render
const FilterInput: React.FC<{
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ placeholder, value, onChange }) => (
  <input
    type="text"
    placeholder={placeholder}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full px-3 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
  />
);

const FilterSelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder: string;
}> = ({ value, onChange, options, placeholder }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full px-3 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
  >
    <option value="">{placeholder}</option>
    {options.map((option) => (
      <option key={option} value={option}>
        {option}
      </option>
    ))}
  </select>
);

const CaseListTable: React.FC<CaseListTableProps> = ({
  cases,
  showPagination = false,
  processingCases = [],
  showActions = true,
}) => {
  const { user } = useAuth();
  const { getErrorsForCase } = useStore();
  const [sortField, setSortField] = useState<SortField>('created');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);

  // Immediate input values (what user types)
  const [inputValues, setInputValues] = useState<Filters>({
    caseNumber: '',
    customer: '',
    status: '',
  });

  // Debounced filter values (what actually filters the table)
  const [filters, setFilters] = useState<Filters>({
    caseNumber: '',
    customer: '',
    status: '',
  });

  const [showFilters, setShowFilters] = useState(false);

  const itemsPerPage = 10;

  // Debounce filter updates: 400ms delay + 3 character minimum
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters({
        caseNumber:
          inputValues.caseNumber.length >= 3 || inputValues.caseNumber.length === 0
            ? inputValues.caseNumber
            : '',
        customer:
          inputValues.customer.length >= 3 || inputValues.customer.length === 0
            ? inputValues.customer
            : '',
        status: inputValues.status, // Status filter is immediate (no minimum)
      });
      setCurrentPage(1); // Reset page when filter actually applies
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [inputValues]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handleFilterChange = (field: keyof Filters, value: string) => {
    setInputValues((prev) => ({ ...prev, [field]: value }));
    // Note: setCurrentPage is called in the debounced effect, not here
  };

  const clearFilters = () => {
    setInputValues({
      caseNumber: '',
      customer: '',
      status: '',
    });
    setFilters({
      caseNumber: '',
      customer: '',
      status: '',
    });
    setCurrentPage(1);
  };

  const filteredAndSortedCases = useMemo(() => {
    let filtered = cases.filter((caseItem) => {
      return (
        caseItem.caseNumber.toLowerCase().includes(filters.caseNumber.toLowerCase()) &&
        caseItem.customer.toLowerCase().includes(filters.customer.toLowerCase()) &&
        caseItem.status.toLowerCase().includes(filters.status.toLowerCase())
      );
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return sortDirection === 'asc' ? 1 : -1;
      if (bValue === null) return sortDirection === 'asc' ? -1 : 1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return filtered;
  }, [cases, filters, sortField, sortDirection]);

  const paginatedCases = showPagination
    ? filteredAndSortedCases.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      )
    : filteredAndSortedCases;

  const totalPages = Math.ceil(filteredAndSortedCases.length / itemsPerPage);
  const hasActiveFilters = Object.values(filters).some((filter) => filter !== '');

  const getStatusColor = (status: PayrollCase['status']) => {
    switch (status) {
      case 'Pending':
        return 'bg-blue-100 text-blue-800';
      case 'In Progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'Processing':
        return 'bg-orange-100 text-orange-800';
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return 'text-green-600';
    if (confidence >= 75) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Enhanced confidence badge with full 5-level architecture (Task #12)
  const getEnhancedConfidenceBadge = (caseItem: PayrollCase) => {
    const evaluation = evaluateCase(caseItem);

    const levelConfig = {
      auto_process: {
        icon: <Cpu size={12} />,
        text: 'Auto-Process',
        className: 'bg-purple-100 text-purple-800 border-purple-200',
        tooltip: 'Automated processing - no CSR review required',
      },
      high: {
        icon: <CheckCircle2 size={12} />,
        text: 'High',
        className: 'bg-green-100 text-green-800 border-green-200',
        tooltip: 'High confidence - quick review required',
      },
      medium: {
        icon: <Shield size={12} />,
        text: 'Medium',
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        tooltip: 'Medium confidence - standard review required',
      },
      low: {
        icon: <AlertTriangle size={12} />,
        text: 'Low',
        className: 'bg-orange-100 text-orange-800 border-orange-200',
        tooltip: 'Low confidence - enhanced review required',
      },
      critical: {
        icon: <Flag size={12} />,
        text: 'Critical',
        className: 'bg-red-100 text-red-800 border-red-200',
        tooltip: 'Critical - manual escalation required',
      },
    };

    const config = levelConfig[evaluation.level] || levelConfig.medium;

    return {
      ...config,
      workflow: evaluation.workflow,
      estimatedTime: evaluation.estimatedTime,
      requiresFlag: evaluation.requiresFlag,
      confidence: caseItem.aiConfidence,
    };
  };

  // Legacy function for backward compatibility
  const getConfidenceBadge = (confidence: number) => {
    const level = getConfidenceLevel(confidence);
    switch (level) {
      case 'high':
        return {
          icon: <CheckCircle2 size={12} />,
          text: 'High',
          className: 'bg-green-100 text-green-800 border-green-200',
        };
      case 'medium':
        return {
          icon: <Shield size={12} />,
          text: 'Medium',
          className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        };
      case 'low':
        return {
          icon: <AlertTriangle size={12} />,
          text: 'Low',
          className: 'bg-red-100 text-red-800 border-red-200',
        };
    }
  };

  // Enhanced workflow indicator (Task #12)
  const getWorkflowIndicator = (caseItem: PayrollCase) => {
    const evaluation = evaluateCase(caseItem);

    const workflowConfig = {
      AUTO_PROCESS: {
        icon: <Cpu size={12} />,
        text: 'Automated',
        className: 'bg-purple-50 text-purple-700 border-purple-200',
        timeIcon: <Clock size={10} />,
        time: '~2 min',
      },
      HIGH_CONFIDENCE: {
        icon: <Workflow size={12} />,
        text: 'Quick Review',
        className: 'bg-green-50 text-green-700 border-green-200',
        timeIcon: <Clock size={10} />,
        time: '~5 min',
      },
      MEDIUM_CONFIDENCE: {
        icon: <BarChart3 size={12} />,
        text: 'Standard Review',
        className: 'bg-yellow-50 text-yellow-700 border-yellow-200',
        timeIcon: <Clock size={10} />,
        time: '~15 min',
      },
      LOW_CONFIDENCE: {
        icon: <Shield size={12} />,
        text: 'Enhanced Review',
        className: 'bg-orange-50 text-orange-700 border-orange-200',
        timeIcon: <Clock size={10} />,
        time: '~30 min',
      },
      MANUAL_ESCALATION: {
        icon: <Flag size={12} />,
        text: 'Escalated',
        className: 'bg-red-50 text-red-700 border-red-200',
        timeIcon: <Clock size={10} />,
        time: 'Manual',
      },
    };

    const config =
      workflowConfig[evaluation.workflow as keyof typeof workflowConfig] ||
      workflowConfig['MEDIUM_CONFIDENCE'];

    return {
      ...config,
      requiresCSR: evaluation.requiresCSR,
      flagged: evaluation.requiresFlag,
    };
  };

  const getPriorityIcon = (priority: PayrollCase['priority']) => {
    switch (priority) {
      case 'high':
        return (
          <div title="High Priority">
            <Flag size={14} className="text-red-500" />
          </div>
        );
      case 'medium':
        return (
          <div title="Medium Priority">
            <Zap size={14} className="text-yellow-500" />
          </div>
        );
      case 'low':
        return (
          <div title="Low Priority">
            <Flag size={14} className="text-gray-400" />
          </div>
        );
      default:
        return null;
    }
  };

  const getCaseTypeDisplay = (caseType?: string) => {
    if (!caseType) {
      return {
        icon: <AlertTriangle size={14} />,
        text: 'Unknown',
        className: 'bg-gray-100 text-gray-600 border-gray-200',
      };
    }

    const typeConfig: Record<
      string,
      { icon: React.ReactNode; text: string; className: string }
    > = {
      Phone: {
        icon: <Phone size={14} />,
        text: 'Phone',
        className: 'bg-blue-100 text-blue-700 border-blue-200',
      },
      Email: {
        icon: <Mail size={14} />,
        text: 'Email',
        className: 'bg-purple-100 text-purple-700 border-purple-200',
      },
      Web: {
        icon: <Globe size={14} />,
        text: 'Web',
        className: 'bg-green-100 text-green-700 border-green-200',
      },
      'Social Media': {
        icon: <MessageSquare size={14} />,
        text: 'Social',
        className: 'bg-pink-100 text-pink-700 border-pink-200',
      },
      Chat: {
        icon: <MessageCircle size={14} />,
        text: 'Chat',
        className: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      },
      Mail: {
        icon: <Mail size={14} />,
        text: 'Mail',
        className: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      },
      Internal: {
        icon: <Building2 size={14} />,
        text: 'Internal',
        className: 'bg-orange-100 text-orange-700 border-orange-200',
      },
      'My Support Hub': {
        icon: <Laptop size={14} />,
        text: 'Hub',
        className: 'bg-teal-100 text-teal-700 border-teal-200',
      },
      Project: {
        icon: <Flag size={14} />,
        text: 'Project',
        className: 'bg-cyan-100 text-cyan-700 border-cyan-200',
      },
    };

    return (
      typeConfig[caseType] || {
        icon: <AlertTriangle size={14} />,
        text: caseType,
        className: 'bg-gray-100 text-gray-600 border-gray-200',
      }
    );
  };

  const SortHeader: React.FC<{
    field: SortField;
    children: React.ReactNode;
  }> = ({ field, children }) => (
    <th
      className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:bg-neutral-50"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortField === field ? (
          sortDirection === 'asc' ? (
            <ChevronUp size={16} />
          ) : (
            <ChevronDown size={16} />
          )
        ) : (
          <ArrowUpDown size={16} className="opacity-50" />
        )}
      </div>
    </th>
  );

  if (cases.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-neutral-400 text-lg mb-2">No cases found</div>
        <div className="text-neutral-500 text-sm">
          There are no payroll cases to display.
        </div>
      </div>
    );
  }

  const uniqueStatuses = Array.from(new Set(cases.map((c) => c.status)));

  return (
    <div className="px-6 pt-6">
      {/* Filter Controls */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
              showFilters || hasActiveFilters
                ? 'bg-primary text-white border-primary'
                : 'bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50'
            }`}
          >
            <Filter size={16} />
            <span>Filters</span>
          </button>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center space-x-2 px-3 py-2 text-sm text-neutral-600 hover:text-neutral-800"
            >
              <X size={14} />
              <span>Clear Filters</span>
            </button>
          )}
        </div>

        <div className="text-sm text-neutral-600">
          Showing {paginatedCases.length} of {filteredAndSortedCases.length} cases
        </div>
      </div>

      {/* Filter Row */}
      {showFilters && (
        <div className="mb-4 p-4 bg-neutral-50 rounded-lg border">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-neutral-700 mb-1">
                Case Number
                <span className="text-xs text-neutral-500 ml-1">(min 3 chars)</span>
              </label>
              <FilterInput
                placeholder="Filter by case number..."
                value={inputValues.caseNumber}
                onChange={(value) => handleFilterChange('caseNumber', value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-700 mb-1">
                Customer
                <span className="text-xs text-neutral-500 ml-1">(min 3 chars)</span>
              </label>
              <FilterInput
                placeholder="Filter by customer..."
                value={inputValues.customer}
                onChange={(value) => handleFilterChange('customer', value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-700 mb-1">
                Status
              </label>
              <FilterSelect
                value={inputValues.status}
                onChange={(value) => handleFilterChange('status', value)}
                options={uniqueStatuses}
                placeholder="All Statuses"
              />
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-neutral-200">
          <thead className="bg-neutral-50">
            <tr>
              {showActions && (
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
              <SortHeader field="caseNumber">Case Number</SortHeader>
              <SortHeader field="customer">Customer</SortHeader>
              <SortHeader field="status">Status</SortHeader>
              <SortHeader field="caseType">Type</SortHeader>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                Errors
              </th>
              <SortHeader field="aiConfidence">AI Confidence</SortHeader>
              <SortHeader field="created">Created</SortHeader>
              <SortHeader field="assignedTo">Assigned CSR</SortHeader>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-neutral-200">
            {paginatedCases.map((caseItem) => {
              const isAssignedToCurrentUser = caseItem.assignedTo === user?.name;

              return (
                <tr
                  key={caseItem.caseNumber}
                  className={`hover:bg-neutral-50 ${
                    isAssignedToCurrentUser
                      ? 'bg-blue-50 border-l-4 border-l-primary'
                      : ''
                  }`}
                >
                  {/* Actions Column - Moved to first position */}
                  {showActions && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        {/* DEMO: Allow anyone to review any case (removed assignment check) */}
                        {/* Original: (isAssignedToCurrentUser || API_CONFIG.ENVIRONMENT === 'production') */}
                        {true &&
                          (() => {
                            // Determine processing status ONLY from polling data
                            // If case is not in processingCases, it's NOT actively processing
                            const processingInfo = processingCases?.find(
                              (pc) => pc.caseNumber === caseItem.caseNumber
                            );

                            // Only use processingInfo if it exists - don't fall back to stale caseItem data
                            const processingStatus =
                              processingInfo?.processingStatus || 'n/a';
                            const currentStage = processingInfo?.currentStage || null;

                            // Determine button state
                            const isProcessing =
                              processingStatus === 'pending' ||
                              processingStatus === 'processing';
                            const isFailed = processingStatus === 'failed';
                            const isDisabled = isProcessing;

                            // Generate tooltip text based on processing status
                            let tooltip = 'Review Case';
                            if (processingStatus === 'pending') {
                              tooltip = 'Queued for processing...';
                            } else if (processingStatus === 'processing') {
                              if (currentStage === 'ocr_extraction') {
                                tooltip = 'Processing OCR...';
                              } else if (currentStage === 'llm_extraction') {
                                tooltip = 'Processing LLM...';
                              } else {
                                tooltip = 'Processing document...';
                              }
                            } else if (processingStatus === 'failed') {
                              tooltip = 'Processing failed - Review for details';
                            }

                            // Render button based on state
                            if (isDisabled) {
                              // Disabled state: cannot click, show spinner
                              return (
                                <Tooltip content={tooltip} delay={200}>
                                  <button
                                    disabled
                                    className="bg-gray-400 text-white px-3 py-1 rounded text-xs cursor-not-allowed flex items-center space-x-1"
                                  >
                                    <Loader className="w-3 h-3 animate-spin" />
                                    <span>Review</span>
                                  </button>
                                </Tooltip>
                              );
                            } else {
                              // Enabled state: can click
                              const buttonClass = isFailed
                                ? 'bg-yellow-500 text-white px-3 py-1 rounded text-xs hover:bg-yellow-600 transition-colors flex items-center space-x-1'
                                : 'bg-orange-500 text-white px-3 py-1 rounded text-xs hover:bg-orange-600 transition-colors flex items-center space-x-1';

                              return (
                                <Tooltip content={tooltip} delay={200}>
                                  <Link to={`/case/${caseItem.caseNumber}`}>
                                    <button className={buttonClass}>
                                      {isFailed && (
                                        <AlertTriangle className="w-3 h-3" />
                                      )}
                                      <span>Review</span>
                                    </button>
                                  </Link>
                                </Tooltip>
                              );
                            }
                          })()}
                      </div>
                    </td>
                  )}

                  {/* Case Number Column */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {caseItem.priority && getPriorityIcon(caseItem.priority)}
                      {caseItem.hasAttachments && (
                        <Paperclip size={14} className="text-neutral-400" />
                      )}
                      <Link
                        to={`/case/${caseItem.caseNumber}`}
                        className="text-sm font-medium text-secondary hover:text-secondary-600"
                      >
                        {caseItem.caseNumber}
                      </Link>
                      {isAssignedToCurrentUser && (
                        <div title="Assigned to you">
                          <User size={16} className="text-primary" />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-neutral-900">{caseItem.customer}</div>
                    {caseItem.description && (
                      <div className="text-sm text-neutral-500 truncate max-w-xs">
                        {caseItem.description}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(caseItem.status)}`}
                    >
                      {caseItem.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {(() => {
                      const typeDisplay = getCaseTypeDisplay(caseItem.caseType);
                      return (
                        <span
                          className={`inline-flex items-center space-x-1.5 px-2.5 py-1 text-xs font-medium rounded-md border ${typeDisplay.className}`}
                        >
                          {typeDisplay.icon}
                          <span>{typeDisplay.text}</span>
                        </span>
                      );
                    })()}
                  </td>

                  {/* Error Indicator Column */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ErrorIndicator
                      errors={getErrorsForCase(caseItem.caseNumber)}
                      caseNumber={caseItem.caseNumber}
                      compact={true}
                      showRecoveryActions={false}
                    />
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <span
                          className={`text-sm font-medium ${getConfidenceColor(caseItem.aiConfidence)}`}
                        >
                          {caseItem.aiConfidence.toFixed(2)}%
                        </span>
                        <div className="ml-1 w-16 bg-neutral-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              caseItem.aiConfidence >= 90
                                ? 'bg-green-500'
                                : caseItem.aiConfidence >= 75
                                  ? 'bg-yellow-500'
                                  : 'bg-red-500'
                            }`}
                            style={{ width: `${caseItem.aiConfidence}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {/* Enhanced Confidence Badge (Task #12) */}
                        {(() => {
                          const badge = getEnhancedConfidenceBadge(caseItem);
                          return (
                            <div className="group relative" title={badge.tooltip}>
                              <span
                                className={`inline-flex items-center space-x-1 px-2 py-0.5 text-xs font-medium rounded-md border ${badge.className}`}
                              >
                                {badge.icon}
                                <span>{badge.text}</span>
                                <span className="text-[10px] opacity-75">
                                  ({badge.confidence}%)
                                </span>
                              </span>

                              {/* Enhanced Tooltip */}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 whitespace-nowrap">
                                <div className="font-medium">{badge.tooltip}</div>
                                <div className="text-gray-300">
                                  Workflow: {badge.workflow}
                                </div>
                                <div className="text-gray-300">
                                  Est. Time: {badge.estimatedTime}
                                </div>
                                {badge.requiresFlag && (
                                  <div className="text-yellow-300">
                                    ⚠ Requires flagging
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Workflow Indicator */}
                        {(() => {
                          const workflow = getWorkflowIndicator(caseItem);
                          return (
                            <div
                              className="group relative"
                              title={`${workflow.text} - ${workflow.time}`}
                            >
                              <span
                                className={`inline-flex items-center space-x-1 px-2 py-0.5 text-xs font-medium rounded-md border ${workflow.className}`}
                              >
                                {workflow.icon}
                                <span className="hidden sm:inline">
                                  {workflow.text}
                                </span>
                                <span className="flex items-center space-x-1 text-[10px]">
                                  {workflow.timeIcon}
                                  <span>{workflow.time}</span>
                                </span>
                              </span>

                              {/* Workflow Tooltip */}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 whitespace-nowrap">
                                <div className="font-medium">
                                  Processing Path: {workflow.text}
                                </div>
                                <div className="text-gray-300">
                                  Estimated Time: {workflow.time}
                                </div>
                                <div className="text-gray-300">
                                  CSR Review:{' '}
                                  {workflow.requiresCSR ? 'Required' : 'Not Required'}
                                </div>
                                {workflow.flagged && (
                                  <div className="text-red-300">
                                    🚩 Flagged for review
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                    {format(new Date(caseItem.created), 'MMM dd, yyyy')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-neutral-900">
                      {caseItem.assignedTo}
                      <div className="text-xs text-neutral-400 mt-1">
                        Pre-assigned from Dynamics
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showPagination && totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-neutral-200 sm:px-6 mt-4">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-neutral-700">
                Showing{' '}
                <span className="font-medium">
                  {(currentPage - 1) * itemsPerPage + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, filteredAndSortedCases.length)}
                </span>{' '}
                of <span className="font-medium">{filteredAndSortedCases.length}</span>{' '}
                results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-neutral-300 bg-white text-sm font-medium text-neutral-500 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const pageNumber = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                  if (pageNumber > totalPages) return null;
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pageNumber === currentPage
                          ? 'z-10 bg-primary border-primary text-white'
                          : 'bg-white border-neutral-300 text-neutral-500 hover:bg-neutral-50'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-neutral-300 bg-white text-sm font-medium text-neutral-500 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseListTable;
